import { message, Modal } from 'ant-design-vue';
import manifest from '@/access_manifest.json';
import { CrmProjectType } from '@/enums/crmProjectType';
import { NinjaExpertLevel } from '@/enums/ninjaExpertLevel';
import { hasTransitionAccess } from '@/helpers/accessWorkflow';
import { api } from '@/api';
import { PlanStatus } from '@/enums/planStatus';
import { PlanType } from '@/enums/planType';
import { getWidgetRoute } from '@/router/helpers';
import { rotationModes } from '@/enums/rotationMode';
import showDeclineNotificationResult from '@/helpers/declineNotification';
import getFlag from '@/helpers/getFlag';
import confirmDeclineWithReason from '@/helpers/declineReasonModal';

const getActionNames = (type) => {
  switch (type) {
    case PlanType.Type2d:
      return {
        statusActionName: 'status',
        startVerificationActionName: 'start_verification',
      };
    case PlanType.Type3d:
      return {
        statusActionName: 'status_3d',
        startVerificationActionName: 'start_verification_3d',
      };
    default:
      throw Error('Undefined Plan Type.');
  }
};

const getActionsByType = (type) => {
  const { statusActionName, startVerificationActionName } = getActionNames(type);

  const actions = {
    StartVerification: {
      text: 'Стать проверяющим',
      type: 'default',
      action: `${startVerificationActionName}`,
    },
    StartFix: {
      text: 'Начать исправление',
      type: 'default',
      action: `${statusActionName}/${PlanStatus.InProgress}`,
    },
    SendOnVerification: {
      text: 'Отправить на проверку',
      type: 'default',
      action: `${statusActionName}/${PlanStatus.AwaitingVerification}`,
    },
    PassFor2dProjectReview: {
      text: 'Сдать',
      type: 'primary',
      action: `${statusActionName}/${PlanStatus.AwaitingVerification}`,
    },
    Pause: {
      text: 'Поставить на паузу',
      type: 'danger',
      action: `${statusActionName}/${PlanStatus.OnPause}`,
    },
    Accept: {
      text: 'Одобрить',
      type: 'primary',
      action: `${statusActionName}/${PlanStatus.Done}`,
    },
    Approve: {
      text: 'Одобрить',
      type: 'primary',
      action: `${statusActionName}/${PlanStatus.Approved}`,
    },
    Reject: {
      text: 'Отклонить',
      type: 'danger',
      action: `${statusActionName}/${PlanStatus.VerificationFailed}`,
    },
    PassForSemiExpertNinja: {
      text: 'Сдать',
      type: 'primary',
      action: `${statusActionName}/${PlanStatus.Done}`,
    },
    PassForExpertNinja: {
      text: 'Сдать',
      type: 'primary',
      action: `${statusActionName}/${PlanStatus.Approved}`,
    },
    SendOnAdjustment: {
      text: 'На корректировку',
      type: 'geekblue',
      action: `${statusActionName}/${PlanStatus.OnAdjustment}`,
    },
    Delete: {
      text: 'Удалить',
      type: 'danger',
      action: 'delete',
    },
    Clone: {
      text: 'Дублировать',
      type: 'default',
      action: 'clone',
    },
    Return: {
      text: 'Вернуть в нарезку',
      type: 'danger',
      action: 'return',
    },
    Decline: {
      text: 'Не принимать в работу',
      type: 'danger',
      action: 'decline',
    },
    OpenReviewWorkspace: {
      text: 'Открыть проверку',
      type: 'default',
      action: 'open_review_workspace',
    },
    OpenFixWorkspace: {
      text: 'Открыть исправление',
      type: 'default',
      action: 'open_fix_workspace',
    },
  };

  if (type === PlanType.Type3d) {
    delete actions.Decline;
    delete actions.Clone;
  }

  return actions;
};

const executeAction = (action, planId, type, callback, router, currentStatus = null) => {
  const executeStatusAction = () => api.executeCrmPlanAction({ planId, action })
    .then(callback)
    .catch((error) => {
      const messageText = error?.response?.data?.message || 'Не удалось выполнить действие';
      message.error(messageText);
    });

  if (action === 'delete') {
    const redirectRouteName = type === PlanType.Type3d ? 'crm-plans3d' : 'crm-plans';

    Modal.confirm({
      centered: true,
      title: 'Вы уверены, что хотите удалить планировку?',
      cancelText: 'Отмена',
      okText: 'Удалить',
      okType: 'danger',
      onOk: () => {
        api.deleteCrmPlan(planId).then(() => router.push({ name: redirectRouteName }));
      },
    });

    return;
  }

  if (action === 'clone') {
    const redirectRouteName = type === PlanType.Type3d ? 'crm-plans3d-show' : 'crm-plans-show';

    Modal.confirm({
      centered: true,
      title: 'Вы уверены, что хотите дублировать планировку?',
      cancelText: 'Отмена',
      okText: 'Дублировать',
      onOk: () => {
        api.cloneCrmPlan(planId).then(({ data }) => {
          router.push({ name: redirectRouteName, params: { id: data.id } });
        });
        callback();
      },
    });

    return;
  }

  if (action === 'return') {
    Modal.confirm({
      centered: true,
      title: 'Вы уверены, что хотите вернуть планировку в нарезку?',
      cancelText: 'Отмена',
      okText: 'Да',
      onOk: () => {
        message.loading('Планировка отправляется в нарезку');
        api.returnCuttingPlan(planId);
      },
    });

    return;
  }

  if (action === 'decline') {
    confirmDeclineWithReason().then((reason) => {
      api.declineCuttingPlan(planId, reason).then(({ data }) => {
        showDeclineNotificationResult(data?.customer_notification);
        router.push({ path: '/crm/plans' });
      });
    });

    return;
  }

  if (action === 'open_review_workspace') {
    router.push({
      name: 'crm-review-show',
      params: { id: planId },
      query: { type },
    });
    return;
  }

  if (action === 'open_fix_workspace') {
    router.push({
      name: 'crm-fix-show',
      params: { id: planId },
      query: { type },
    });
    return;
  }

  const normalizedStatus = currentStatus?.id ?? Number(currentStatus);
  const failedAction = action === `status/${PlanStatus.VerificationFailed}`
    || action === `status_3d/${PlanStatus.VerificationFailed}`;

  if (failedAction && normalizedStatus === PlanStatus.AwaitingVerification) {
    Modal.confirm({
      centered: true,
      title: 'Завершить проверку?',
      content: 'Проверка завершится, а планировка будет отправлена ниндзе на исправление.',
      cancelText: 'Отмена',
      okText: 'Отправить',
      okType: 'danger',
      onOk: executeStatusAction,
    });
    return;
  }

  if (action === 'status_3d/3') {
    Modal.confirm({
      centered: true,
      title: 'Вы уверены, что проверили тур перед сдачей?',
      cancelText: 'Проверить',
      okText: 'Да',
      onOk: executeStatusAction,
      onCancel: () => {
        const widgetUrl = getWidgetRoute({
          crmPlanId: planId,
          rotationMode: rotationModes.THREESIXTY,
          locale: 'ru',
        });
        window.open(widgetUrl, '_blank');
      },
    });
    return;
  }

  executeStatusAction();
};

const hasPlanPermission = (user, permission) => user.roles?.some(({ name }) => name === 'owner')
  || (user.permissions ?? []).includes(permission);

const getStatusActions = (type, plan, user) => {
  const entity = type === PlanType.Type3d ? 'plan_3d' : 'plan_2d';
  const status = Number(plan.status?.id ?? plan.status);
  const prefix = type === PlanType.Type3d ? 'status_3d' : 'status';
  const allowed = Object.entries(manifest.entities[entity].statuses)
    .filter(([target]) => Number(target) !== status && hasTransitionAccess(user, entity, status, target)
      && (!Array.isArray(plan.available_status_transitions)
        || plan.available_status_transitions.includes(Number(target))));
  const labels = {
    [PlanStatus.InProgress]: 'Начать исправление',
    [PlanStatus.AwaitingVerification]: 'Отправить на проверку',
    [PlanStatus.VerificationFailed]: 'Отклонить',
    [PlanStatus.Done]: 'Одобрить',
    [PlanStatus.Approved]: 'Одобрить',
    [PlanStatus.OnAdjustment]: 'На корректировку',
    [PlanStatus.OnPause]: 'Поставить на паузу',
    [PlanStatus.Declined]: 'Не принимать в работу',
  };
  const actionType = (target) => {
    if ([3, 5, 6].includes(Number(target))) return 'primary';
    return [4, 8, -2].includes(Number(target)) ? 'danger' : 'default';
  };
  return allowed.map(([target, label]) => ({
    text: labels[target] ?? `Перевести в «${label}»`,
    type: actionType(target),
    action: `${prefix}/${target}`,
  }));
};

const getDirectActions = (type, plan, user, actions) => {
  const permissions = {
    StartVerification: type === PlanType.Type3d
      ? 'api.crm.plans.start_verification_3d' : 'api.crm.plans.start_verification',
    Delete: 'api.crm.plans.delete',
    Clone: 'api.crm.plans.replicate',
    Return: 'api.crm.plans.cropping.return',
    Decline: 'api.crm.plans.decline',
  };
  return Object.entries(permissions).filter(([key, permission]) => {
    if (key === 'StartVerification' && plan.manager !== null) return false;
    if (key === 'Return' && Number(plan.status?.id ?? plan.status) !== PlanStatus.ToDo) return false;
    return actions[key] && hasPlanPermission(user, permission);
  }).map(([key]) => actions[key]);
};

const getWorkspaceActions = (user, plan, actions) => {
  const result = [];
  const canObserve = hasPlanPermission(user, 'crm.reviews.observe')
    || hasPlanPermission(user, 'crm.reviews.manage_others');
  if (hasPlanPermission(user, 'ui.page.crm-review-show')
    && (canObserve || (plan.reviewer?.id ?? plan.reviewer_id) === user.id)) result.push(actions.OpenReviewWorkspace);
  if (hasPlanPermission(user, 'ui.page.crm-fix-show')
    && (canObserve || (plan.ninja?.id ?? plan.ninja_id) === user.id)) result.push(actions.OpenFixWorkspace);
  return result;
};

const pickManagerPlanActions = (type, plan) => {
  const actions = getActionsByType(type);

  if (plan.manager === null) {
    return [actions.StartVerification, actions.Delete, actions.Clone];
  }

  const status = plan.status && plan.status.id ? plan.status.id : Number(plan.status);

  if (status === PlanStatus.OnPause) {
    return [actions.StartFix, actions.SendOnVerification];
  }

  if (status === PlanStatus.InProgress) {
    return [actions.Pause];
  }

  if (status === PlanStatus.AwaitingVerification) {
    return [actions.Accept, actions.Reject, actions.Delete, actions.Clone, actions.Pause];
  }

  if (status === PlanStatus.Done) {
    return [actions.Reject, actions.Delete, actions.Clone];
  }

  return [actions.Delete, actions.Clone];
};

const pickNinjaPlanActions = (type, plan, user) => {
  const actions = getActionsByType(type);

  const status = plan.status && plan.status.id ? plan.status.id : Number(plan.status);
  const ninjaId = plan.ninja?.id ?? plan.ninja_id;
  const qualityReviewIteration = Number(plan.quality_review_iteration ?? 1);
  const qualityReviewPanelsVisible = getFlag('isQualityReviewPanelsVisible');
  const qualityAction = qualityReviewPanelsVisible && user.id === ninjaId && qualityReviewIteration >= 2 && [
    PlanStatus.VerificationFailed,
    PlanStatus.InProgress,
  ].includes(status)
    ? [actions.OpenFixWorkspace]
    : [];

  if (status === PlanStatus.VerificationFailed) {
    return [...qualityAction, actions.StartFix, actions.Clone];
  }

  const projectType = plan.apartment?.project_type;
  if (
    type === PlanType.Type2d
    && status === PlanStatus.InProgress
    && projectType !== undefined
    && projectType !== null
  ) {
    return [
      Number(projectType) === CrmProjectType.Type2d
        ? actions.PassFor2dProjectReview
        : actions.PassForExpertNinja,
      actions.Clone,
    ];
  }

  if (type === PlanType.Type3d && status === PlanStatus.InProgress) {
    return [...qualityAction, actions.SendOnVerification];
  }

  let level = NinjaExpertLevel.Common;
  if (type === PlanType.Type2d) {
    level = user.ninja_expert_level_2d ?? NinjaExpertLevel.Common;
  } else if (type === PlanType.Type3d) {
    level = user.ninja_expert_level_3d ?? NinjaExpertLevel.Common;
  }

  if (level === NinjaExpertLevel.Common && status === PlanStatus.InProgress) {
    return [...qualityAction, actions.SendOnVerification, actions.Clone];
  }

  if (level === NinjaExpertLevel.Semi && status === PlanStatus.InProgress) {
    return [...qualityAction, actions.PassForSemiExpertNinja, actions.Clone];
  }

  if (level === NinjaExpertLevel.Expert && status === PlanStatus.InProgress) {
    return [...qualityAction, actions.PassForExpertNinja, actions.Clone];
  }

  return [actions.Clone];
};

const pickServicePlanActions = (type, plan) => {
  const actions = getActionsByType(type);

  const status = plan.status && plan.status.id ? plan.status.id : Number(plan.status);

  if (status === PlanStatus.Done) {
    return [actions.Approve, actions.Reject];
  }
  if (status === PlanStatus.Approved) {
    return [actions.Reject];
  }

  return [];
};

const pickAdminPlanActions = (type, plan) => {
  const actions = getActionsByType(type);
  const status = plan.status && plan.status.id ? plan.status.id : Number(plan.status);
  const qualityActions = [];
  const qualityReviewEnabled = getFlag('isQualityReviewPanelsVisible');

  // Админ заходит в обе панели любой планировки и в любом статусе — сначала только на просмотр,
  // поэтому статус здесь ничего не решает.
  if (qualityReviewEnabled) {
    qualityActions.push(
      { ...actions.OpenReviewWorkspace, text: 'Панель проверки' },
      { ...actions.OpenFixWorkspace, text: 'Панель исправления' },
    );
  }

  delete actions.OpenReviewWorkspace;
  delete actions.OpenFixWorkspace;
  delete actions.PassFor2dProjectReview;

  if (status !== PlanStatus.ToDo) {
    delete actions.Return;
  }
  return [...qualityActions, ...Object.values(actions)];
};

const pickReviewerPlanActions = (type, plan, user) => {
  const actions = getActionsByType(type);
  const status = plan.status && plan.status.id ? plan.status.id : Number(plan.status);
  const reviewerId = plan.reviewer?.id ?? plan.reviewer_id;

  if (getFlag('isQualityReviewPanelsVisible') && user.id === reviewerId && [
    PlanStatus.AwaitingVerification,
    PlanStatus.VerificationFailed,
    PlanStatus.InProgress,
  ].includes(status)) {
    return [actions.OpenReviewWorkspace, actions.Clone];
  }

  return [actions.Clone];
};

const getLegacyPlanActions = (type, plan, user) => {
  const status = Number(plan.status?.id ?? plan.status);
  if (type === PlanType.Type3d && status === PlanStatus.Deactivated) return [];
  const userRolesNames = (user.roles ?? []).map(({ name }) => name.toLowerCase());

  if (userRolesNames.includes('admin') || userRolesNames.includes('owner')) {
    return pickAdminPlanActions(type, plan).filter((i) => i);
  }

  if (userRolesNames.includes('manager')) {
    return pickManagerPlanActions(type, plan).filter((i) => i);
  }

  if (userRolesNames.includes('ninja')) {
    return pickNinjaPlanActions(type, plan, user).filter((i) => i);
  }

  if (userRolesNames.includes('service')) {
    return pickServicePlanActions(type, plan).filter((i) => i);
  }

  if (userRolesNames.includes('reviewer')) {
    return pickReviewerPlanActions(type, plan, user).filter((i) => i);
  }

  return [];
};

const getPlanActionsForUser = (type, plan, user) => {
  const status = Number(plan.status?.id ?? plan.status);
  if (type === PlanType.Type3d && status === PlanStatus.Deactivated) return [];
  const available = [
    ...getStatusActions(type, plan, user),
    ...getWorkspaceActions(user, plan, getActionsByType(type)),
    ...getDirectActions(type, plan, user, getActionsByType(type)),
  ];
  const allowed = new Set(available.map(({ action }) => action));
  const privileged = (user.roles ?? []).some(({ name }) => ['admin', 'owner', 'manager'].includes(name))
    || hasPlanPermission(user, 'crm.plans.manage_others');
  const executor = !privileged && ((user.roles ?? []).some(({ name }) => name === 'ninja')
    || (user.id !== undefined && (plan.ninja?.id ?? plan.ninja_id) === user.id));
  const legacy = executor ? pickNinjaPlanActions(type, plan, user)
    : getLegacyPlanActions(type, plan, user);
  const selected = legacy.filter((item) => item && allowed.has(item.action));
  const represented = new Set(selected.map(({ action }) => action));
  const prefix = type === PlanType.Type3d ? 'status_3d' : 'status';
  const submissions = [3, 5, 6].map((target) => `${prefix}/${target}`);
  return [
    ...selected.map((item) => (executor && status === PlanStatus.InProgress
      && submissions.includes(item.action) ? { ...item, text: 'Сдать', type: 'primary' } : item)),
    ...available.filter(({ action }) => !represented.has(action)
      && !(executor && status === PlanStatus.InProgress && submissions.includes(action))),
  ];
};

export {
  getPlanActionsForUser,
  executeAction,
};
